/**
 * Writes an edited line set into every deck in scope.
 *
 * The studio's other deck writes go through `replaceStudioDeck`, which only touches
 * `definition.active_deck` — so a line-set edit could never reach another deck, and an imported report
 * left each deck with whatever statement it started with. This is the one write that may span decks.
 *
 * A deck that does not carry the set yet gets it appended AND its first rows field repointed at it.
 * Without that, "apply to all decks" would store a statement the other decks never render.
 *
 * Pure + React-free (CLAUDE.md separation of concerns).
 */

import type { StudioDeck, StudioLineSet } from 'src/components/star-grid/reportStudio.types';

export const applyLineSetToDecks = (
  decks: Readonly<Record<string, StudioDeck>>,
  targets: readonly string[],
  set: StudioLineSet
): Record<string, StudioDeck> => {
  const next: Record<string, StudioDeck> = { ...decks };
  for (const deck of targets) {
    const current = next[deck];
    if (!current) continue;
    const has = current.line_sets.some((item) => item.id === set.id);
    next[deck] = {
      ...current,
      line_sets: has ? current.line_sets.map((item) => (item.id === set.id ? set : item)) : [...current.line_sets, set],
      rows: has
        ? current.rows
        : current.rows.map((row, index) =>
            index === 0 ? { ...row, binding_id: set.binding_id, selection: { kind: 'line_set' as const, reference_id: set.id, members: [] } } : row
          )
    };
  }
  return next;
};

/** The decks an edit lands on: the chosen ones, else the active deck alone. */
export const lineSetTargets = (chosen: readonly string[], activeDeck: string | undefined): readonly string[] =>
  chosen.length > 0 ? chosen : activeDeck ? [activeDeck] : [];
